import type {
  CommitTransitionInvocation,
  EffectProjectionResult,
  ManifestContent,
  ProjectionChange,
  ProjectionResult,
  RecordObjectOptions,
  ToolWithHandler,
  TransitionProjectionResult,
} from "./types";

export interface RecordSnapshotCommitSource {
  reference: string;
  version?: string;
  versionScope?: "global" | "target";
  beforeRecords: RecordObjectOptions[];
  afterRecords: RecordObjectOptions[];
}

type RelationshipTarget = { className: string };

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, item]) => item !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, stableValue(item)]),
    );
  }
  return value;
}

function stableJson(value: unknown): string {
  return JSON.stringify(stableValue(value));
}

function recordKey(record: Pick<RecordObjectOptions, "className" | "id">) {
  return `${record.className}\u0000${record.id}`;
}

function recordReference(className: string, id: string) {
  return { className, id };
}

/**
 * Keeps only records whose canonical projection changed. This is useful for
 * persisting bounded, replayable mapper input beside a product mutation.
 */
export function changedRecordSnapshots(
  beforeRecords: RecordObjectOptions[],
  afterRecords: RecordObjectOptions[],
): Pick<RecordSnapshotCommitSource, "beforeRecords" | "afterRecords"> {
  const before = new Map(
    beforeRecords.map((record) => [recordKey(record), record]),
  );
  const after = new Map(
    afterRecords.map((record) => [recordKey(record), record]),
  );
  const changedKeys = new Set<string>();
  for (const key of new Set([...before.keys(), ...after.keys()])) {
    if (stableJson(before.get(key)) !== stableJson(after.get(key))) {
      changedKeys.add(key);
    }
  }
  return {
    beforeRecords: beforeRecords.filter((record) =>
      changedKeys.has(recordKey(record)),
    ),
    afterRecords: afterRecords.filter((record) =>
      changedKeys.has(recordKey(record)),
    ),
  };
}

function relationshipTargets(
  manifest: ManifestContent,
): Map<string, RelationshipTarget> {
  const targets = new Map<string, RelationshipTarget>();
  for (const volume of manifest.volumes) {
    for (const operation of volume.operations) {
      const relationship = operation.defineRelationship;
      if (!relationship) continue;
      targets.set(`${relationship.left}\u0000${relationship.leftSubmodel}`, {
        className: relationship.right,
      });
      targets.set(`${relationship.right}\u0000${relationship.rightSubmodel}`, {
        className: relationship.left,
      });
    }
  }
  return targets;
}

function stateName(
  value: NonNullable<RecordObjectOptions["states"]>[string],
): string {
  return typeof value === "string" ? value : value.state;
}

function asIds(value: string | string[] | undefined): string[] {
  return value === undefined ? [] : Array.isArray(value) ? value : [value];
}

function changedObjectProjection(
  before: RecordObjectOptions | undefined,
  after: RecordObjectOptions,
): ProjectionChange | null {
  const beforeFields = before?.fields || {};
  const afterFields = after.fields || {};
  const fields = { ...afterFields };
  for (const fieldName of Object.keys(beforeFields)) {
    if (!(fieldName in afterFields)) fields[fieldName] = null;
  }
  if (
    before &&
    before.label === after.label &&
    stableJson(beforeFields) === stableJson(afterFields)
  ) {
    return null;
  }
  return {
    kind: "object",
    operation: before ? "updated" : "created",
    record: {
      className: after.className,
      id: after.id,
      ...(after.label !== undefined ? { label: after.label } : {}),
      fields,
    },
  };
}

function projectionChanges(input: {
  manifest: ManifestContent;
  beforeRecords: RecordObjectOptions[];
  afterRecords: RecordObjectOptions[];
  transition?: CommitTransitionInvocation;
}): ProjectionChange[] {
  const before = new Map(
    input.beforeRecords.map((record) => [recordKey(record), record]),
  );
  const after = new Map(
    input.afterRecords.map((record) => [recordKey(record), record]),
  );
  const targets = relationshipTargets(input.manifest);
  const changes: ProjectionChange[] = [];

  for (const record of input.afterRecords) {
    const previous = before.get(recordKey(record));
    const objectChange = changedObjectProjection(previous, record);
    if (objectChange) changes.push(objectChange);

    const relationshipNames = new Set([
      ...Object.keys(previous?.relationships || {}),
      ...Object.keys(record.relationships || {}),
    ]);
    for (const relationshipName of [...relationshipNames].sort()) {
      const target = targets.get(
        `${record.className}\u0000${relationshipName}`,
      );
      if (!target) {
        throw new Error(
          `No ontology relationship target is declared for ${record.className}.${relationshipName}`,
        );
      }
      const previousIds = new Set(
        asIds(previous?.relationships?.[relationshipName]),
      );
      const currentIds = new Set(
        asIds(record.relationships?.[relationshipName]),
      );
      for (const id of [...previousIds].sort()) {
        if (currentIds.has(id)) continue;
        changes.push({
          kind: "relationship",
          operation: "disconnected",
          relationship: relationshipName,
          from: recordReference(record.className, record.id),
          to: recordReference(target.className, id),
        });
      }
      for (const id of [...currentIds].sort()) {
        if (previousIds.has(id)) continue;
        changes.push({
          kind: "relationship",
          operation: "connected",
          relationship: relationshipName,
          from: recordReference(record.className, record.id),
          to: recordReference(target.className, id),
        });
      }
    }

    const machines = new Set([
      ...Object.keys(previous?.states || {}),
      ...Object.keys(record.states || {}),
    ]);
    for (const machine of [...machines].sort()) {
      const next = record.states?.[machine];
      if (next === undefined) continue;
      const prior = previous?.states?.[machine];
      if (prior !== undefined && stateName(prior) === stateName(next)) continue;
      if (
        input.transition &&
        input.transition.className === record.className &&
        input.transition.objectId === record.id &&
        input.transition.machine === machine
      ) {
        // The transition commit is the one atomic state mutation for its
        // target. A parallel observation would manufacture a source mismatch.
        continue;
      }
      changes.push({
        kind: "state_observation",
        target: recordReference(record.className, record.id),
        machine,
        state: stateName(next),
      });
    }
  }

  for (const record of input.beforeRecords) {
    if (after.has(recordKey(record))) continue;
    changes.push({
      kind: "object",
      operation: "deleted",
      target: recordReference(record.className, record.id),
    });
  }
  return changes;
}

function valueAtPath(value: unknown, path: string): unknown {
  return path
    .split(".")
    .filter(Boolean)
    .reduce<unknown>((current, segment) => {
      if (!current || typeof current !== "object" || Array.isArray(current)) {
        return undefined;
      }
      return (current as Record<string, unknown>)[segment];
    }, value);
}

function primaryTarget(input: {
  effect: ToolWithHandler;
  effectInput: unknown;
  result: unknown;
  changes: ProjectionChange[];
  transition?: CommitTransitionInvocation;
}) {
  if (input.transition) {
    return recordReference(
      input.transition.className,
      input.transition.objectId,
    );
  }
  const creates = input.effect.metamodels?.creates;
  if (creates) {
    const declaration =
      typeof creates === "string" ? { className: creates } : creates;
    const id = declaration.idPath
      ? valueAtPath(input.result, declaration.idPath)
      : undefined;
    if (typeof id === "string" && id) {
      return recordReference(declaration.className, id);
    }
  }
  if (input.effect.className && !input.effect.static) {
    const objectId =
      input.effectInput &&
      typeof input.effectInput === "object" &&
      !Array.isArray(input.effectInput)
        ? (input.effectInput as Record<string, unknown>)._objectId
        : undefined;
    if (typeof objectId === "string" && objectId) {
      return recordReference(input.effect.className, objectId);
    }
  }
  const created = input.changes.filter(
    (
      change,
    ): change is Extract<ProjectionChange, { kind: "object" }> & {
      operation: "created";
    } => change.kind === "object" && change.operation === "created",
  );
  const onlyCreated = created.length === 1 ? created[0] : undefined;
  return onlyCreated
    ? recordReference(onlyCreated.record.className, onlyCreated.record.id)
    : undefined;
}

function transitionOutcome(
  transition: CommitTransitionInvocation | undefined,
  afterRecords: RecordObjectOptions[],
): TransitionProjectionResult["outcome"] {
  if (!transition) {
    throw new Error("A transition commit has no authored transition context.");
  }
  const target = afterRecords.find(
    (record) =>
      record.className === transition.className &&
      record.id === transition.objectId,
  );
  const observed = target?.states?.[transition.machine];
  if (!observed) {
    throw new Error(
      `The product result did not expose ${transition.className}:${transition.objectId}.${transition.machine}`,
    );
  }
  const finalState = stateName(observed);
  const matches = Object.entries(transition.outcomes).filter(
    ([, outcome]) =>
      outcome.to === finalState ||
      (outcome.to === "$current" && finalState === transition.from),
  );
  if (matches.length !== 1) {
    throw new Error(
      `Product state ${finalState} maps to ${matches.length} authored outcomes for ${transition.machine}.${transition.transition}`,
    );
  }
  const [key, declaration] = matches[0]!;
  return {
    key,
    ...(declaration.disposition === "error"
      ? {
          error: {
            code: `product_outcome_${key}`,
            message:
              declaration.label ||
              `The product completed in ${finalState} instead of continuing.`,
            retryable: false,
          },
        }
      : {}),
  };
}

/**
 * Deterministically maps a persisted before/after product snapshot to commit
 * changes. It performs no I/O and never guesses ontology labels or paths.
 */
export function projectRecordSnapshotMutation(input: {
  manifest: ManifestContent;
  effect: ToolWithHandler;
  effectInput: unknown;
  result: unknown;
  source: RecordSnapshotCommitSource;
  commitKind: "effect" | "transition";
  transition?: CommitTransitionInvocation;
}): ProjectionResult {
  const changes = projectionChanges({
    manifest: input.manifest,
    beforeRecords: input.source.beforeRecords,
    afterRecords: input.source.afterRecords,
    transition: input.transition,
  });
  const target = primaryTarget({
    effect: input.effect,
    effectInput: input.effectInput,
    result: input.result,
    changes,
    transition: input.transition,
  });
  const base: EffectProjectionResult = {
    source: {
      reference: input.source.reference,
      ...(input.source.version ? { version: input.source.version } : {}),
      ...(input.source.versionScope
        ? { versionScope: input.source.versionScope }
        : {}),
    },
    ...(target ? { primaryTarget: target } : {}),
    changes,
    safeSummary: {
      effect: input.effect.name,
      affectedChanges: changes.length,
    },
  };
  if (input.commitKind === "effect") return base;
  return {
    ...base,
    outcome: transitionOutcome(input.transition, input.source.afterRecords),
  };
}
